import React, {useCallback, useContext} from 'react';
import {useNavigate} from 'react-router-dom';


import {ConfirmationContext} from 'src/contexts/confirmation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {AccountSettingsController} from 'src/modules/profile/account/tabs/settings/account-settings-controller';

import Button from '@pb/kit-component-button';

import {CreateAccountStep} from '../create-account-step';
import {CREATE_ACCOUNT_STEPS} from '../steps';

import type {ConfirmationContextType} from 'src/contexts/confirmation-context';

type Props = {
    accountId: number,
    onNext: () => void,
    onBack: () => void,
    onRedirectToStep: (step: CREATE_ACCOUNT_STEPS) => void,
};

export const CreateAccountSettings = ({accountId, onNext, onBack, onRedirectToStep}: Props) => {
    const navigate = useNavigate();
    const {onShowModal} = useContext<ConfirmationContextType>(ConfirmationContext);
    const {accountCreateSubmit, accountFieldChange} = useEventTracking();
    const {canCreateLicense} = useAdminAccessRight();

    const onTrackSubmit = useCallback((action: string) => {
        accountCreateSubmit({
            action,
            place: 'Account Settings',
        });
    }, [accountCreateSubmit]);

    const onClose = useCallback(() => {
        navigate(`/profile/account/${accountId}/`);
    }, [accountId, navigate]);

    const onFieldChange = (section: string) => (parameterName: string) => {
        accountFieldChange({
            parameterName,
            section,
            viewType: 'New',
        });
    };

    return (
        <ValidationContextProvider>
            <AccountSettingsController {...{
                accountId,
                onFieldChange,
                onProceed: canCreateLicense(accountId)
                    ? onNext
                    : onClose,
            }}>
                {({content, saveBusy, onSave, isChunkChanged}) => (
                    <CreateAccountStep {...{
                        selectedStep: CREATE_ACCOUNT_STEPS.ACCOUNT_SETTINGS,
                        onChangeStep: step => {
                            if (isChunkChanged) {
                                onShowModal(() => onRedirectToStep(step));
                            } else {
                                onRedirectToStep(step);
                            }
                        },
                        content,
                        buttonsBlock: [
                            <Button
                                key="Close"
                                variant="Type.TERTIARY"
                                onClick={() => {
                                    onClose();
                                    onTrackSubmit('Close');
                                }}>
                                Close
                            </Button>,
                            <Button
                                key="Back"
                                variant="Type.SECONDARY"
                                onClick={() => {
                                    if (isChunkChanged) {
                                        onShowModal(onBack);
                                    } else {
                                        onBack();
                                    }
                                }}>
                                Back
                            </Button>,
                            <Button
                                key="Next"
                                busy={saveBusy}
                                variant="Type.PRIMARY"
                                onClick={() => {
                                    if (!canCreateLicense) {
                                        onTrackSubmit('Finish');
                                    }
                                    onSave();
                                }}>
                                {canCreateLicense(accountId) ? 'Save & Continue' : 'Finish'}
                            </Button>,
                        ],
                    }}
                    />
                )}
            </AccountSettingsController>
        </ValidationContextProvider>
    );
};
